import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import {
  Button,
  Input,
  Textarea,
  Checkbox,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  IconButton,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServiceWafCatalog,
  useServiceWafCustomStrategies,
  waapAsArray,
  useCreateServiceCustomPolicy,
  useUpdateServiceCustomPolicy,
  CUSTOM_TEMPLATES,
  ACTION_BLOCK,
  ACTION_MONITOR,
  type CustomTemplate,
  type CustomPolicyPayload,
  type WafCatalogGroup,
  type WafCustomStrategyRow,
  type WafCatalogRule,
  type WafModuleOverride,
  type WafRuleAction,
  type WafRuleOverride,
  type StoredModuleSetting,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './CustomPolicyForm.module.css';

const NAME_MAX = 26;
const DESC_MAX = 200;
const PAGE_SIZE = 25;
/** Search runs over every category at once — Strict is sixteen of them, and SQL
 *  Injection alone holds 119 rules — so wait for the typing to settle, ignore a
 *  single character (it matches nearly everything) and cap what is rendered. */
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARS = 2;
const SEARCH_LIMIT = 200;

/** Strip the vendor's <br/>-laced HTML to plain multi-line text. */
const htmlToText = (s?: string): string =>
  (s ?? '').replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').trim();

const riskTone = (risk?: string): 'danger' | 'warning' | 'neutral' => {
  const r = (risk ?? '').toLowerCase();
  if (r === 'high') return 'danger';
  if (r === 'medium') return 'warning';
  return 'neutral';
};

const levelTone = (level?: string): 'info' | 'warning' | 'neutral' => {
  const l = (level ?? '').toLowerCase();
  if (l === 'caution') return 'warning';
  if (l === 'strict') return 'info';
  return 'neutral';
};

/** A rule plus the group it came from and its effective state. */
interface PolicyRow {
  rule: WafCatalogRule;
  group: WafCatalogGroup;
  selected: boolean;
  action: WafRuleAction;
}

/** Rebuild a module's rule map from what the template currently ticks — the
 *  moment a row is touched the policy switches to this expanded shape. */
function materialise(group: WafCatalogGroup): Record<string, WafRuleOverride> {
  const map: Record<string, WafRuleOverride> = {};
  for (const r of group.rules) {
    if (r.selected) map[String(r.id)] = { enabled: 1, action: r.action };
  }
  return map;
}

/** Read a template out of a stored policy row ("2", 2 or "Standard"). */
function normaliseTemplate(raw: unknown): CustomTemplate {
  const s = String(raw ?? '').toLowerCase();
  if (s === '2' || s === 'standard') return '2';
  if (s === '3' || s === 'strict') return '3';
  return '1';
}

/**
 * Add / edit a Custom WAF Protection Policy.
 *
 * Everything about the policy shape comes from the API: the catalog arrives
 * already annotated for the chosen template, so this screen only decides which
 * tab to show and which rows the user changed. A module is sent as an override
 * only once it is touched — untouched modules keep tracking their template.
 */
export function CustomPolicyForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId, policyId } = useParams<{ serviceId: string; policyId?: string }>();
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;
  const editing = !!policyId;

  // The list row travels with the Edit click; on a direct hit of the URL it is
  // not there, so fall back to reading it out of the account's policy list.
  const passed = (location.state as { policy?: WafCustomStrategyRow } | null)?.policy;
  const list = useServiceWafCustomStrategies(editing && !passed ? serviceId : undefined);
  const stored = useMemo(() => {
    if (passed) return passed;
    return waapAsArray<WafCustomStrategyRow>(list.data).find(
      (r) => String(r.id) === String(policyId),
    );
  }, [passed, list.data, policyId]);

  const [name, setName] = useState(String(passed?.name ?? ''));
  const [description, setDescription] = useState(String(passed?.comment ?? ''));
  const [template, setTemplate] = useState<CustomTemplate>(normaliseTemplate(passed?.template));

  /** Per-module rule maps for the modules the user touched. */
  const [ruleOverrides, setRuleOverrides] = useState<Record<string, Record<string, WafRuleOverride>>>({});
  /** Explicit tab on/off from the settings popover, keyed by tag. */
  const [tagEnabled, setTagEnabled] = useState<Record<string, boolean>>({});
  /** Explicit semantics toggles, keyed by syntax key. */
  const [syntaxEnabled, setSyntaxEnabled] = useState<Record<string, boolean>>({});

  const [activeTag, setActiveTag] = useState('');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchQuery.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const catalog = useServiceWafCatalog(serviceId, template);
  const groups = useMemo(() => catalog.data?.groups ?? [], [catalog.data]);

  // Editing: fill the header fields as soon as the policy is known…
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current || !editing || !stored) return;
    hydrated.current = true;
    setName(String(stored.name ?? ''));
    setDescription(String(stored.comment ?? ''));
    setTemplate(normaliseTemplate(stored.template));
  }, [editing, stored]);

  // …then seed the toggles once that template's catalog is in, so the form opens
  // on exactly what was saved, per-rule actions included.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !editing || !stored || !groups.length) return;
    seeded.current = true;
    const modules = (stored.module_strategies ?? {}) as Record<string, StoredModuleSetting>;
    const rules: Record<string, Record<string, WafRuleOverride>> = {};
    const tags: Record<string, boolean> = {};
    for (const g of groups) {
      if (g.kind !== 'module' || !g.module) continue;
      const m = modules[g.module];
      if (!m) continue;
      tags[g.tag] = !!m.enabled;
      if (m.rules) rules[g.module] = m.rules as Record<string, WafRuleOverride>;
    }
    const syntax: Record<string, boolean> = {};
    for (const [key, v] of Object.entries(
      (stored.syntax_strategies ?? {}) as Record<string, StoredModuleSetting>,
    )) {
      syntax[key] = !!v.enabled;
    }
    setRuleOverrides(rules);
    setTagEnabled(tags);
    setSyntaxEnabled(syntax);
  }, [editing, stored, groups]);

  const isTagOn = (g: WafCatalogGroup) => tagEnabled[g.tag] ?? g.enabled;
  const visibleGroups = useMemo(() => groups.filter(isTagOn), [groups, tagEnabled]);

  // Keep the active tab on a tab that still exists.
  useEffect(() => {
    if (!visibleGroups.length) return;
    if (!visibleGroups.some((g) => g.tag === activeTag)) {
      setActiveTag(visibleGroups[0].tag);
      setPage(1);
    }
  }, [visibleGroups, activeTag]);

  /** Switching template re-derives every toggle, exactly as the portal does. */
  const pickTemplate = (t: CustomTemplate) => {
    setTemplate(t);
    setRuleOverrides({});
    setTagEnabled({});
    setSyntaxEnabled({});
    setFilter('');
    setPage(1);
  };

  /** The effective state of one rule: an override wins, otherwise the template. */
  const rowOf = (group: WafCatalogGroup, rule: WafCatalogRule): PolicyRow => {
    if (!isTagOn(group)) return { rule, group, selected: false, action: rule.action };
    if (group.kind === 'syntax' && rule.syntaxKey && rule.syntaxKey in syntaxEnabled) {
      return { rule, group, selected: syntaxEnabled[rule.syntaxKey], action: rule.action };
    }
    const map = group.module ? ruleOverrides[group.module] : undefined;
    if (map) {
      const ov = map[String(rule.id)];
      return {
        rule,
        group,
        selected: !!ov && ov.enabled !== 0,
        action: ov?.action ?? rule.action,
      };
    }
    return { rule, group, selected: rule.selected, action: rule.action };
  };

  const setRule = (group: WafCatalogGroup, rule: WafCatalogRule, next: Partial<PolicyRow>) => {
    if (group.kind === 'syntax') {
      if (!rule.syntaxKey) return;
      const current = rowOf(group, rule);
      setSyntaxEnabled((prev) => ({
        ...prev,
        [rule.syntaxKey as string]: next.selected ?? current.selected,
      }));
      return;
    }
    if (!group.module) return;
    const key = group.module;
    setRuleOverrides((prev) => {
      const map = { ...(prev[key] ?? materialise(group)) };
      const id = String(rule.id);
      const current = map[id];
      const selected = next.selected ?? (!!current && current.enabled !== 0);
      const action = next.action ?? current?.action ?? rule.action;
      if (selected) map[id] = { enabled: 1, action };
      else delete map[id];
      return { ...prev, [key]: map };
    });
  };

  const rowsFor = (group: WafCatalogGroup): PolicyRow[] => group.rules.map((r) => rowOf(group, r));

  const activeGroup = visibleGroups.find((g) => g.tag === activeTag) ?? visibleGroups[0];
  const activeRows = activeGroup ? rowsFor(activeGroup) : [];

  const filteredRows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return activeRows;
    return activeRows.filter(
      (r) => String(r.rule.id).includes(q) || (r.rule.name ?? '').toLowerCase().includes(q),
    );
  }, [activeRows, filter]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const allOnPageSelected = pageRows.length > 0 && pageRows.every((r) => r.selected);

  /** Count ticked rules per tab, honouring local edits. */
  const selectedCount = (g: WafCatalogGroup) => rowsFor(g).filter((r) => r.selected).length;

  // ---- Search Policy: every rule across every visible tab ------------------
  const searchMatches = useMemo(() => {
    const q = searchTerm.toLowerCase();
    if (q.length < SEARCH_MIN_CHARS) return [] as PolicyRow[];
    const out: PolicyRow[] = [];
    for (const g of visibleGroups) {
      for (const r of g.rules) {
        if (
          String(r.id).includes(q) ||
          (r.name ?? '').toLowerCase().includes(q) ||
          g.tag.toLowerCase().includes(q)
        ) {
          out.push(rowOf(g, r));
        }
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, visibleGroups, ruleOverrides, syntaxEnabled, tagEnabled]);

  const searchRows = searchMatches.slice(0, SEARCH_LIMIT);
  const searchSelected = searchMatches.filter((r) => r.selected).length;
  const searchPending = searchQuery.trim() !== searchTerm;

  // ---- Save ---------------------------------------------------------------
  const buildPayload = (): CustomPolicyPayload => {
    const payload: CustomPolicyPayload = {
      name: name.trim(),
      comment: description.trim(),
      template,
    };
    const modules: Record<string, WafModuleOverride> = {};
    for (const g of groups) {
      if (g.kind !== 'module' || !g.module) continue;
      const on = isTagOn(g);
      const rules = ruleOverrides[g.module];
      if (rules) modules[g.module] = { enabled: on && Object.keys(rules).length > 0, rules };
      else if (on !== g.enabled) modules[g.module] = { enabled: on };
    }
    if (Object.keys(modules).length) payload.module_strategies = modules;

    const syntax: Record<string, WafModuleOverride> = {};
    const semantics = groups.find((g) => g.kind === 'syntax');
    if (semantics && !isTagOn(semantics)) {
      for (const r of semantics.rules) if (r.syntaxKey) syntax[r.syntaxKey] = { enabled: false };
    } else {
      for (const [key, enabled] of Object.entries(syntaxEnabled)) syntax[key] = { enabled };
    }
    if (Object.keys(syntax).length) payload.syntax_strategies = syntax;
    return payload;
  };

  const done = (verb: string) => {
    trackFeatureAction('waap', 'custom_waf_policy', editing ? 'edit' : 'create');
    show({ tone: 'success', title: `Custom WAF protection policy ${verb}` });
    navigate(`${base}/aiwaf/general`);
  };
  const failed = (e: unknown) =>
    show({ tone: 'danger', title: 'Save failed', description: errMessage(e) });

  const create = useServiceCreate(serviceId ?? '', () => done('created'), failed);
  const update = useServiceUpdate(serviceId ?? '', () => done('updated'), failed);
  const saving = create.isPending || update.isPending;

  const nameValid = name.trim().length > 0 && name.trim().length <= NAME_MAX;
  const submit = () => {
    const payload = buildPayload();
    if (editing && policyId) update.mutate({ id: policyId, payload });
    else create.mutate(payload);
  };

  // ---- Tab strip scrolling ------------------------------------------------
  const stripRef = useRef<HTMLDivElement | null>(null);
  const scrollStrip = (dir: -1 | 1) => {
    stripRef.current?.scrollBy({ left: dir * 240, behavior: 'smooth' });
  };

  const ruleColumns = (withGroup: boolean): ColumnDef<PolicyRow>[] => {
    const cols: ColumnDef<PolicyRow>[] = [
      {
        id: 'selected',
        size: 44,
        header: withGroup
          ? ''
          : () => (
              <Checkbox
                aria-label="Select all rules on this page"
                checked={allOnPageSelected}
                onCheckedChange={(v) => {
                  for (const row of pageRows) setRule(row.group, row.rule, { selected: v === true });
                }}
              />
            ),
        cell: ({ row }) => (
          <Checkbox
            aria-label={`Select rule ${row.original.rule.id}`}
            checked={row.original.selected}
            onCheckedChange={(v) =>
              setRule(row.original.group, row.original.rule, { selected: v === true })
            }
          />
        ),
      },
    ];
    if (withGroup) {
      cols.push({
        id: 'group',
        header: 'Group',
        size: 132,
        cell: ({ row }) => <span className={styles.name}>{row.original.group.tag}</span>,
      });
    }
    cols.push(
      { id: 'id', header: 'Rule ID', size: withGroup ? 78 : 90, cell: ({ row }) => row.original.rule.id },
      {
        id: 'name',
        header: 'Rule Name',
        size: withGroup ? 176 : 200,
        cell: ({ row }) => <span className={styles.name}>{row.original.rule.name}</span>,
      },
      {
        id: 'description',
        header: 'Description',
        size: withGroup ? 330 : 460,
        cell: ({ row }) => {
          const text = htmlToText(row.original.rule.summary);
          return (
            <span className={styles.ruleDesc} title={text}>
              {text}
            </span>
          );
        },
      },
      {
        id: 'risk',
        header: 'Risk Level',
        size: 92,
        cell: ({ row }) => (
          <StatusPill tone={riskTone(row.original.rule.riskLevel)}>
            {row.original.rule.riskLevel || '—'}
          </StatusPill>
        ),
      },
      {
        id: 'security',
        header: 'Security Level',
        size: withGroup ? 104 : 112,
        cell: ({ row }) => (
          <StatusPill tone={levelTone(row.original.rule.securityLevel)}>
            {row.original.rule.securityLevel || '—'}
          </StatusPill>
        ),
      },
      {
        id: 'action',
        header: 'Action',
        size: withGroup ? 118 : 128,
        cell: ({ row }) =>
          row.original.group.kind === 'syntax' ? (
            <span className={styles.muted}>{row.original.rule.actionLabel}</span>
          ) : (
            <div className={styles.actionSelect}>
              <Select
                value={String(row.original.action)}
                onValueChange={(v) =>
                  setRule(row.original.group, row.original.rule, {
                    selected: true,
                    action: Number(v) as WafRuleAction,
                  })
                }
              >
                <SelectTrigger aria-label={`Action for rule ${row.original.rule.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(ACTION_BLOCK)}>Block</SelectItem>
                  <SelectItem value={String(ACTION_MONITOR)}>Monitor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ),
      },
    );
    return cols;
  };

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'General Protection', slug: 'aiwaf/general' },
              { label: editing ? 'Edit Custom WAF Protection Policy' : 'Add Custom WAF Protection Policy' },
            ]}
          />
        }
        title="Custom WAF Protection Policy"
      />

      <div className={styles.formGrid}>
        <Card flat>
          <CardHeader>
            <CardTitle>Name</CardTitle>
            <CardDescription>
              Enter the name for a new rule. 1-26 characters. Valid: 0-9, A-Z, a-z, space, . _ - ( ) * +
            </CardDescription>
          </CardHeader>
          <CardBody>
            <Field
              label=""
              error={name.length > 0 && !nameValid ? 'Name must be 1-26 characters' : undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  value={name}
                  maxLength={NAME_MAX}
                  placeholder="Policy name"
                  onChange={(e) => setName(e.target.value)}
                />
              )}
            </Field>
          </CardBody>
        </Card>

        <Card flat>
          <CardHeader>
            <CardTitle>Description</CardTitle>
            <CardDescription>The description can have 1-200 characters.</CardDescription>
          </CardHeader>
          <CardBody>
            <div>
              <Textarea
                value={description}
                maxLength={DESC_MAX}
                placeholder="Description"
                onChange={(e) => setDescription(e.target.value)}
              />
              <div className={styles.charCount}>
                {description.length}/{DESC_MAX}
              </div>
            </div>
          </CardBody>
        </Card>

        <Section
          title="Policy Template"
          description="Select a policy template to start customisation."
          actions={
            <Button variant="secondary" size="sm" onClick={() => setSearchOpen(true)}>
              Search Policy
            </Button>
          }
        >
          <div className={styles.templateRow}>
            {CUSTOM_TEMPLATES.map((t) => (
              <Button
                key={t.value}
                variant={template === t.value ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => pickTemplate(t.value)}
              >
                {t.label}
              </Button>
            ))}
          </div>

          {catalog.isLoading ? (
            <Skeleton style={{ height: 240 }} />
          ) : (
            <>
              <div className={styles.tabBar}>
                <IconButton
                  aria-label="Scroll tabs left"
                  variant="ghost"
                  size="sm"
                  onClick={() => scrollStrip(-1)}
                >
                  <ChevronLeft size={16} />
                </IconButton>
                <div className={styles.tabStrip} ref={stripRef}>
                  {visibleGroups.map((g) => (
                    <button
                      key={g.tag}
                      type="button"
                      className={g.tag === activeTag ? styles.tabActive : styles.tab}
                      onClick={() => {
                        setActiveTag(g.tag);
                        setPage(1);
                        setFilter('');
                      }}
                    >
                      {g.tag} ({selectedCount(g)}/{g.total})
                    </button>
                  ))}
                </div>
                <IconButton
                  aria-label="Scroll tabs right"
                  variant="ghost"
                  size="sm"
                  onClick={() => scrollStrip(1)}
                >
                  <ChevronRight size={16} />
                </IconButton>

                <div className={styles.tagSelect}>
                  <Select value={activeTag} onValueChange={(v) => { setActiveTag(v); setPage(1); }}>
                    <SelectTrigger aria-label="Jump to category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {visibleGroups.map((g) => (
                        <SelectItem key={g.tag} value={g.tag}>
                          {g.tag}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Popover>
                  <PopoverTrigger asChild>
                    <IconButton aria-label="Choose categories" variant="secondary" size="sm">
                      <SlidersHorizontal size={16} />
                    </IconButton>
                  </PopoverTrigger>
                  <PopoverContent>
                    <div className={styles.tagList}>
                      {groups.map((g) => (
                        <label key={g.tag} className={styles.tagOption}>
                          <Checkbox
                            checked={isTagOn(g)}
                            onCheckedChange={(v) =>
                              setTagEnabled((prev) => ({ ...prev, [g.tag]: v === true }))
                            }
                          />
                          <span className={isTagOn(g) ? styles.tagOnLabel : styles.muted}>{g.tag}</span>
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              <div className={styles.filterRow}>
                <div className={styles.filterInput}>
                  <Input
                    placeholder="Filter by Rule ID or Rule Name"
                    startSlot={<Search size={16} />}
                    value={filter}
                    onChange={(e) => {
                      setFilter(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
                <span className={styles.filterCount}>
                  {activeGroup
                    ? `${selectedCount(activeGroup)} of ${activeGroup.total} rules selected`
                    : ''}
                  {filter.trim() ? ` · ${filteredRows.length} match this filter` : ''}
                </span>
                <span className={styles.filterCount}>
                  Total {filteredRows.length} · {PAGE_SIZE} records per page
                </span>
              </div>

              <div className={styles.tableScroll}>
              <DataTable
                columns={ruleColumns(false)}
                data={pageRows}
                emptyState={
                  <EmptyState
                    size="sm"
                    icon={<Search size={20} />}
                    title="No rules found"
                    description="No rules match the selected category or search filter."
                  />
                }
              />
              </div>
              {filteredRows.length > 0 && (
                <div className={styles.pagination}>
                  <Pagination page={safePage} pageCount={pageCount} onPageChange={setPage} />
                </div>
              )}
            </>
          )}
        </Section>

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={() => navigate(`${base}/aiwaf/general`)}>
            Cancel
          </Button>
          <Button disabled={!nameValid || saving} onClick={submit}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className={styles.searchDialog}>
          <DialogHeader>
            <DialogTitle>Search Policy</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Input
              placeholder="Search by group, Rule ID or Rule Name"
              startSlot={<Search size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className={styles.matched}>
              {searchPending
                ? 'Searching…'
                : `Matched: ${searchSelected}/${searchMatches.length}`}
              {searchMatches.length > SEARCH_LIMIT
                ? ` · showing the first ${SEARCH_LIMIT}`
                : ''}
            </div>
            <div className={`${styles.tableScroll} ${styles.searchTable}`}>
            <DataTable
              columns={ruleColumns(true)}
              data={searchRows}
              emptyState={
                <EmptyState
                  size="sm"
                  icon={<Search size={20} />}
                  title="No rules found"
                  description={`Type at least ${SEARCH_MIN_CHARS} characters of a group name, rule id or rule name to search every category.`}
                />
              }
            />
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </Container>
  );
}

/** Create mutation wired to the shared success/failure handlers. */
function useServiceCreate(serviceId: string, onDone: () => void, onFail: (e: unknown) => void) {
  return useCreateServiceCustomPolicy(serviceId, { onSuccess: onDone, onError: onFail });
}

/** Update mutation wired to the shared success/failure handlers. */
function useServiceUpdate(serviceId: string, onDone: () => void, onFail: (e: unknown) => void) {
  return useUpdateServiceCustomPolicy(serviceId, { onSuccess: onDone, onError: onFail });
}
